import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { blogAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import { CalendarIcon, ArrowLeftIcon, ShareIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';

const BlogDetail = () => {
    const { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const response = await blogAPI.getById(id);
                setBlog(response.data);
            } catch (error) {
                console.error("Error fetching blog:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlog();
    }, [id]);

    if (loading) return <div className="text-center py-20">Loading...</div>;
    if (!blog) return <div className="text-center py-20">Blog post not found.</div>;

    // Handle media urls (legacy single or new array)
    let mediaList = [];
    if (blog.media_urls && Array.isArray(blog.media_urls) && blog.media_urls.length > 0) {
        mediaList = blog.media_urls;
    } else if (blog.media_url) {
        mediaList = [blog.media_url];
    }

    // Normalize paths
    mediaList = mediaList.map(url => getImgUrl(url));

    // For social preview choose second image when available (user prefers second image)
    // Note: social platforms fetch OG tags from the server (they may not execute client JS).
    // To ensure previews always pick the right image for crawlers, use SSR/prerendering.
    const previewImage = mediaList.length > 1 ? mediaList[1] : (mediaList[0] || null);

    const shareUrl = window.location.href;
    const title = blog.title;

    const handleShare = (platform) => {
        let url = '';
        switch (platform) {
            case 'whatsapp':
                url = `https://wa.me/?text=${encodeURIComponent(title + ' ' + shareUrl)}`;
                break;
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`;
                break;
            case 'copy':
                navigator.clipboard.writeText(shareUrl);
                alert('Link copied to clipboard!');
                return;
            default:
                break;
        }
        if (url) window.open(url, '_blank');
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % mediaList.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + mediaList.length) % mediaList.length);
    };

    return (
        <div className="bg-white min-h-screen pt-24 pb-12 font-sans selection:bg-green-100 selection:text-green-900">
            <Helmet>
                <title>{blog.title} | Marvelous Agri Solution</title>
                <meta name="description" content={blog.content.substring(0, 150)} />
                <meta property="og:title" content={blog.title} />
                <meta property="og:description" content={blog.content.substring(0, 150)} />
                {previewImage && <meta property="og:image" content={previewImage} />}
                {previewImage && <meta name="twitter:image" content={previewImage} />}
                <meta property="og:url" content={shareUrl} />
                <meta property="og:type" content="article" />
            </Helmet>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Link */}
                <Link to="/blogs" className="inline-flex items-center text-gray-500 hover:text-green-600 mb-8 transition-colors group">
                    <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-sm font-medium tracking-wide">Back to News</span>
                </Link>

                {/* Article Header */}
                <header className="mb-10 text-center md:text-left">
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-gray-500 mb-6 uppercase tracking-wider font-semibold">
                        <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                            {blog.media_type || 'News'}
                        </span>
                        <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            <span>{new Date(blog.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                    </div>

                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-8 leading-tight tracking-tight">
                        {blog.title}
                    </h1>

                    {/* Social Share Bar */}
                    {/* <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 border-y border-gray-100 py-6">
                        <span className="text-sm font-bold text-gray-900 flex items-center gap-2 mr-2">
                            <ShareIcon className="h-4 w-4" /> Share this:
                        </span>
                        <button onClick={() => handleShare('whatsapp')} className="p-2 rounded-full bg-green-50 text-green-600 hover:bg-green-100 transition-colors" title="Share on WhatsApp">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                        </button>
                        <button onClick={() => handleShare('facebook')} className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Share on Facebook">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                        </button>
                        <button onClick={() => handleShare('copy')} className="px-4 py-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs font-bold transition-colors ml-auto md:ml-0" title="Copy Link">
                            COPY LINK
                        </button>
                    </div> */}
                </header>

                {/* Media Carousel */}
                {mediaList.length > 0 && (
                    <div className="mb-12 relative group rounded-3xl overflow-hidden shadow-2xl bg-gray-100">
                        <div className="relative aspect-[16/9] md:aspect-[21/9]">
                            <AnimatePresence mode='wait'>
                                <motion.div
                                    key={currentSlide}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="w-full h-full"
                                >
                                    {blog.media_type === 'video' ? (
                                        <video src={mediaList[currentSlide]} controls className="w-full h-full object-contain" />
                                    ) : (
                                        <img src={mediaList[currentSlide]} alt={`${blog.title} - ${currentSlide + 1}`} className="w-full h-full object-cover" />
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {mediaList.length > 1 && (
                            <>
                                <button
                                    onClick={prevSlide}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/80 backdrop-blur-sm p-2 rounded-full text-white hover:text-gray-900 transition-all opacity-0 group-hover:opacity-100"
                                >
                                    <ArrowLeftIcon className="h-6 w-6" />
                                </button>
                                <button
                                    onClick={nextSlide}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/80 backdrop-blur-sm p-2 rounded-full text-white hover:text-gray-900 transition-all opacity-0 group-hover:opacity-100"
                                >
                                    <ArrowLeftIcon className="h-6 w-6 rotate-180" />
                                </button>
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                    {mediaList.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentSlide(index)}
                                            className={`w-2 h-2 rounded-full transition-all ${index === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
                                        />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Article Content */}
                <article className="prose prose-lg prose-green mx-auto max-w-none text-gray-700 leading-relaxed font-serif">
                    {blog.content ? blog.content.split('\n').map((paragraph, idx) => (
                        <p key={idx} className="mb-6">{paragraph}</p>
                    )) : (
                        <p className="text-gray-500 italic">No content available for this post.</p>
                    )}
                </article>

                {/* Small bottom share bar + Author/Footer */}
                <div className="mt-12 mb-6 p-4 rounded-lg bg-gray-50 border border-gray-100 shadow-sm">
                    <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-4">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-900 flex items-center gap-2">
                                <ShareIcon className="h-4 w-4 text-green-600" /> Share this:
                            </span>
                            <button onClick={() => handleShare('whatsapp')} className="p-2 rounded-md bg-green-50 text-green-600 hover:bg-green-100 transition-colors" title="Share on WhatsApp">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                            </button>
                            <button onClick={() => handleShare('facebook')} className="p-2 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title="Share on Facebook">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                            </button>
                            <button onClick={() => handleShare('twitter')} className="p-2 rounded-md bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors" title="Share on Twitter">
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557a9.83 9.83 0 01-2.828.775 4.932 4.932 0 002.165-2.724 9.86 9.86 0 01-3.127 1.195 4.916 4.916 0 00-8.38 4.482A13.944 13.944 0 011.671 3.149 4.916 4.916 0 003.194 9.86a4.902 4.902 0 01-2.228-.616v.06a4.915 4.915 0 003.946 4.814 4.996 4.996 0 01-2.224.085 4.918 4.918 0 004.588 3.417A9.868 9.868 0 010 19.54a13.94 13.94 0 007.548 2.212c9.058 0 14.01-7.513 14.01-14.01 0-.213-.005-.425-.014-.636A10.025 10.025 0 0024 4.557z" /></svg>
                            </button>
                        </div>

                        <div className="ml-auto text-sm text-gray-500">
                            <p className="hidden md:block">Marvelous Agri Solution</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between text-gray-500 text-sm">
                    <p className="text-sm">© Marvelous Agri Solution</p>
                    <Link to="/blogs" className="font-bold text-green-600 hover:underline">Read more news</Link>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
